import type { Doc } from "building-types/doc";
import type { BuiltInLabel, Label, LabelableEntityBase } from "building-types/entities/labelTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LabelableEntityHandle } from "building-types/handles/labelableEntityHandle";
import { LabelableDocEntityTypes } from "building-types/handles/labelableEntityHandle";
import type { RWTable, Table } from "common/data/table";
import objectFilter from "common/utils/objectFilter";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { docEntityHandleIsLabelable } from "../entity/docEntityHandleTools";
import { docForEachEntityHandleOfType } from "../entity/docForEachEntity";
import { docEntityGetComponentOrAnnotationHost } from "../entity/docGetEntityComponent";
import { docGetLabelTable } from "../entity/docGetLabelTable";
import { roofBlocksComponentInstanceChain } from "../roofSystemTools";

export function entitiesContainMatchingLabel(entityA: LabelableEntityBase, entityB: LabelableEntityBase): boolean {
  return Object.keys(entityA.labels).some((label) => entityA.labels[label] && entityB.labels[label]);
}

/** Returns true if a label table contains no true values (can contain false or undefined) */
export function labelTableContainsNoLabels(entity: LabelableEntityBase): boolean {
  const labels = objectFilter(entity.labels, Boolean);
  return objectIsEmpty(labels);
}

export function labelFilterPredicate(hostEntity: LabelableEntityBase, entity: LabelableEntityBase): boolean {
  if (labelTableContainsNoLabels(hostEntity)) {
    return true;
  }

  return entitiesContainMatchingLabel(hostEntity, entity);
}

/** Labels must have non-empty, unique names */
export function labelNamePredicate(labels: Table<Label>, { name }: Partial<Label>): boolean {
  if (name === undefined) {
    return true;
  }

  return name !== "" && !Object.values(labels).some((label) => label.name === name);
}

/**
 * @deprecated
 * Provides type safety when creating a label table with built-in labels.
 *
 * TODO: remove this function when switching to a fully generalized filtering system.
 */
export function makeBuiltInLabelTable(...labels: BuiltInLabel[]): Table<boolean> {
  const table: RWTable<boolean> = {};
  for (const label of labels) {
    table[label] = true;
  }
  return table;
}

export function docEntityContainsLabel(doc: Doc, handle: LabelableEntityHandle, labelId: string): boolean {
  const entityLabels = docGetLabelTable(doc, handle);
  return !!entityLabels[labelId];
}

export const builtInLabelDisplayNames: { [label in BuiltInLabel]: string } = {
  addToElectricalPlan: "Electrical Plan",
  addToFloorPlan: "Floor Plan",
  addToFoundationPlan: "Foundation Plan",
  addToStructuralPlan: "Structural Plan",
  addToShowroomPlan: "Showroom Plan",
  addToBrochurePlan: "Brochure Plan",
  addToFloorMaterialsPlan: "Floor Materials Plan",
  addToFloorSummaryPlan: "Area Summary Plan",
  addToRoofPlan: "Roof Plan",
  addToUtilityPlan: "Utility Plan",
  addToExteriorElevation: "Exterior Elevation",
  addToPerspectiveView: "Perspective View",
};

/**
 * Checks if a label ID is a reportable unit designator label.
 */
export function isReportableUnitDesignatorLabel(
  doc: Pick<Doc, "reportableUnitDesignations">,
  labelId: string,
): boolean {
  return Object.values(doc.reportableUnitDesignations).some((designation) => designation.labelId === labelId);
}

/** Base reporting unit info extracted from a Doc. */
export interface DocReportingUnit {
  readonly id: string;
  readonly name: string;
  readonly sortIndex: number;
}

/** Internal helper: get sorted designations */
function getSortedReportableUnitDesignations(doc: Pick<Doc, "reportableUnitDesignations">) {
  return Object.values(doc.reportableUnitDesignations ?? {}).sort((a, b) => a.sortIndex - b.sortIndex);
}

/**
 * Gets all reportable unit designator label IDs.
 */
export function getReportableUnitDesignatorLabelIds(doc: Pick<Doc, "reportableUnitDesignations">): string[] {
  return getSortedReportableUnitDesignations(doc).map((designation) => designation.labelId);
}

/**
 * Returns the ordered list of reporting units for a doc.
 * Reporting units are derived from ReportableUnitDesignations linked to Labels.
 * The labelId serves as the reporting unit ID at the doc level.
 */
export function getReportingUnitsFromDoc(doc: Pick<Doc, "reportableUnitDesignations" | "labels">): DocReportingUnit[] {
  const units: DocReportingUnit[] = [];
  for (const designation of getSortedReportableUnitDesignations(doc)) {
    const label = doc.labels[designation.labelId];
    if (label) {
      units.push({
        id: designation.labelId,
        name: label.name,
        sortIndex: designation.sortIndex,
      });
    }
  }
  return units;
}

/**
 * Reporting unit IDs (label IDs) for a doc, restricted to designations whose label still exists.
 * Prefer this over `getReportableUnitDesignatorLabelIds`, which returns the raw designator label
 * IDs including orphaned designations whose label has since been deleted.
 */
export function reportingUnitIdsFromDoc(doc: Pick<Doc, "reportableUnitDesignations" | "labels">): string[] {
  return getReportingUnitsFromDoc(doc).map((unit) => unit.id);
}

/**
 * Returns reporting units with display names for default unit naming behavior:
 * use label name when non-empty, otherwise fallback to positional "Unit N".
 */
export function getReportingUnitsWithDefaultNamesFromDoc(
  doc: Pick<Doc, "reportableUnitDesignations" | "labels">,
): DocReportingUnit[] {
  const units = getReportingUnitsFromDoc(doc);

  return units.map((unit, index) => {
    if (unit.name && unit.name.trim().length > 0) {
      return unit;
    }

    const name = `Unit ${index + 1}`;
    return { ...unit, name };
  });
}

/**
 * Checks if an entity has any reportable unit designator label.
 */
export function docEntityHasAnyReportableUnitDesignatorLabel(doc: Doc, handle: LabelableEntityHandle): boolean {
  const reportableUnitDesignatorLabelIds = getReportableUnitDesignatorLabelIds(doc);
  const entityLabels = docGetLabelTable(doc, handle);
  return reportableUnitDesignatorLabelIds.some((labelId) => entityLabels[labelId]);
}

/**
 * Gets all labelable entities that have a specific label.
 */
export function docGetLabelableEntitiesWithLabel(doc: Doc, labelId: string): LabelableEntityHandle[] {
  const result: LabelableEntityHandle[] = [];

  for (const entityType of LabelableDocEntityTypes) {
    docForEachEntityHandleOfType(doc, entityType, (handle) => {
      if (docEntityHandleIsLabelable(handle)) {
        const labels = docGetLabelTable(doc, handle);
        if (labels[labelId]) {
          result.push(handle);
        }
      }
    });
  }

  return result;
}

/**
 * True when a roof is nested within a component instance that carries a reportable unit label.
 * The instance is referenced through the roof's block chain (virtual roof block level usage or
 * instance-scoped block refs), not through component membership, so it is not detected by
 * `docEntityGetComponent`.
 */
function docRoofNestedInReportableUnitComponentInstance(doc: Doc, handle: DocEntityHandle): boolean {
  if (handle.type !== "ROOF") {
    return false;
  }
  const roof = doc.roofs[handle.id];
  if (!roof) {
    return false;
  }
  return roofBlocksComponentInstanceChain(roof.blocks).some((componentInstanceId) =>
    docEntityHasAnyReportableUnitDesignatorLabel(doc, { type: "COMPONENT_INSTANCE", id: componentInstanceId }),
  );
}

/**
 * True when a component instance contains a roof that carries a reportable unit label. This is the
 * reverse of `docRoofNestedInReportableUnitComponentInstance`: it guards against labelling the
 * instance when a nested roof already carries one, so the invalid state cannot be reached from
 * either direction.
 */
function docComponentInstanceContainsReportableUnitRoof(doc: Doc, handle: DocEntityHandle): boolean {
  if (handle.type !== "COMPONENT_INSTANCE") {
    return false;
  }
  return Object.values(doc.roofs).some(
    (roof) =>
      roofBlocksComponentInstanceChain(roof.blocks).includes(handle.id) &&
      docEntityHasAnyReportableUnitDesignatorLabel(doc, { type: "ROOF", id: roof.id }),
  );
}

/**
 * Main validation function that checks for reportable unit label conflicts.
 * Returns true if there is a conflict, false otherwise.
 *
 * ConflictRule: No entity with a reportable unit label can be a member of any component, nor may
 * a roof and a component instance both carry a reportable unit label when the roof is nested
 * within that instance (guarded from both the roof and the component instance side).
 *
 * @param doc - The document
 * @param handle - The entity handle to check (can be any DocEntityHandle, but labels are only checked for labelable entities)
 * @param proposedLabelId - Label ID being added. Checks if adding this label would create a conflict.
 */
export function docEntityHasReportableUnitLabelConflict(
  doc: Doc,
  handle: DocEntityHandle,
  proposedLabelId: string,
): boolean {
  const reportableUnitDesignatorLabelIds = getReportableUnitDesignatorLabelIds(doc);
  if (reportableUnitDesignatorLabelIds.length === 0) {
    return false;
  }

  // Check if we're adding a reportable unit designator label
  const isAddingReportableUnitDesignatorLabel = proposedLabelId
    ? isReportableUnitDesignatorLabel(doc, proposedLabelId)
    : false;

  // Get entity labels if labelable, otherwise empty
  const entityLabels = docEntityHandleIsLabelable(handle) ? docGetLabelTable(doc, handle) : {};
  const hasReportableUnitDesignatorLabel =
    reportableUnitDesignatorLabelIds.some((labelId) => entityLabels[labelId]) || isAddingReportableUnitDesignatorLabel;

  if (!hasReportableUnitDesignatorLabel) {
    return false;
  }

  // Conflict: entity has (or is getting) a reportable unit label AND is a component member or is an
  // annotation hosted in a component
  const componentHandle = docEntityGetComponentOrAnnotationHost(doc, handle);
  if (componentHandle) {
    return true;
  }

  // Conflict: a roof with a reportable unit label nested within a component instance that also
  // carries one (the roof references the instance through its block chain, not membership).
  if (docRoofNestedInReportableUnitComponentInstance(doc, handle)) {
    return true;
  }

  // Conflict (reverse direction): a component instance gaining a reportable unit label while a
  // nested roof already carries one.
  if (docComponentInstanceContainsReportableUnitRoof(doc, handle)) {
    return true;
  }

  return false;
}
